import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Gem, Rocket, Crown, Sparkles } from 'lucide-react';
import type { WheelPrize } from '@/types/gems';

interface WheelPrizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  prize: WheelPrize | null;
}

const getPrizeIcon = (type: string) => {
  switch (type) {
    case 'gems':
      return <Gem className="w-12 h-12 text-purple-400" />;
    case 'boost':
      return <Rocket className="w-12 h-12 text-amber-400" />;
    case 'vip_day':
      return <Crown className="w-12 h-12 text-luxury-pinkBright" />;
    default:
      return <Sparkles className="w-12 h-12 text-amber-400/60" />;
  }
};

const getPrizeMessage = (prize: WheelPrize) => {
  switch (prize.type) {
    case 'gems':
      return `Você ganhou ${prize.value} gema${prize.value > 1 ? 's' : ''}! 💎`;
    case 'boost':
      return `Você ganhou ${prize.value} boost${prize.value > 1 ? 's' : ''}! 🚀`;
    case 'vip_day':
      return 'Parabéns! Você ganhou 1 dia VIP! 👑';
    default:
      return 'O próximo giro pode mudar tudo...';
  }
};

const isWinningPrize = (type: string) => {
  return type !== 'nothing';
};

export const WheelPrizeModal = ({ isOpen, onClose, prize }: WheelPrizeModalProps) => {
  if (!prize) return null;

  const isWin = isWinningPrize(prize.type);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-white/10 max-w-sm mx-auto">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="flex flex-col items-center text-center py-6"
        >
          {/* Prize icon with glow */}
          <motion.div
            initial={{ rotate: -10 }}
            animate={{ rotate: [0, -5, 5, 0] }}
            transition={{ repeat: isWin ? 3 : 0, duration: 0.3 }}
            className="relative mb-6"
          >
            <div 
              className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{ 
                backgroundColor: prize.color + '20',
                boxShadow: isWin ? `0 0 40px ${prize.color}40` : 'none'
              }}
            >
              {getPrizeIcon(prize.type)}
            </div>
            
            {/* Sparkles for winning prizes */}
            {isWin && (
              <>
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="absolute -top-2 -right-2"
                >
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="absolute -bottom-1 -left-2"
                >
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                </motion.div>
              </>
            )}
          </motion.div>

          {/* Prize emoji */}
          <motion.span
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-5xl mb-4"
          >
            {prize.emoji}
          </motion.span>

          {/* Title */}
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className={`text-xl font-bold mb-2 ${isWin ? 'text-white' : 'text-white/60'}`}
          >
            {isWin ? 'Parabéns!' : 'Quase!'}
          </motion.h2>

          {/* Message */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white/80 mb-6"
          >
            {getPrizeMessage(prize)}
          </motion.p>

          {/* Prize label - hide for 'nothing' */}
          {isWin && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: 'spring' }}
              className="px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: prize.color + '30' }}
            >
              <span className="font-semibold" style={{ color: prize.color }}>
                {prize.label}
              </span>
            </motion.div>
          )}

          {/* Close button */}
          <Button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:brightness-110 text-white font-semibold"
          >
            {isWin ? 'Resgatar Prêmio' : 'Girar Novamente 🎰'}
          </Button>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
